import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "convex/react";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "@convex/_generated/api";
import LiquidCalendar from "@/components/LiquidCalendar";
import RecorderPanel from "@/components/RecorderPanel";
import {
  dateKeyOf,
  visibleRange,
  type CalendarLesson,
  type CalendarView,
} from "@/lib/calendar";
import type { LessonStatus } from "@/lib/status";

const SESSION_CURSOR_KEY = "lls_calendar_cursor";
const SESSION_VIEW_KEY = "lls_calendar_view";

/**
 * Owns the calendar cursor so the range query and the grid always agree on
 * which dates are on screen. Everything below is presentational.
 */
export default function Calendar() {
  const [searchParams] = useSearchParams();

  const [cursor, setCursor] = useState<Date>(() => {
    const fromUrl = searchParams.get("date");
    if (fromUrl && /^\d{4}-\d{2}-\d{2}$/.test(fromUrl)) {
      const parsed = new Date(`${fromUrl}T00:00:00`);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    try {
      const saved = sessionStorage.getItem(SESSION_CURSOR_KEY);
      if (saved && /^\d{4}-\d{2}-\d{2}$/.test(saved)) {
        const parsed = new Date(`${saved}T00:00:00`);
        if (!isNaN(parsed.getTime())) return parsed;
      }
    } catch {
      // sessionStorage unavailable
    }
    return new Date();
  });

  const [view, setView] = useState<CalendarView>(() => {
    const fromUrl = searchParams.get("view");
    if (fromUrl === "week" || fromUrl === "month") return fromUrl;
    try {
      const saved = sessionStorage.getItem(SESSION_VIEW_KEY);
      if (saved === "week" || saved === "month") return saved;
    } catch {
      // ignore
    }
    return "week";
  });

  const urlDate = searchParams.get("date");
  const [prevUrlDate, setPrevUrlDate] = useState(urlDate);
  if (urlDate !== prevUrlDate) {
    setPrevUrlDate(urlDate);
    if (urlDate && /^\d{4}-\d{2}-\d{2}$/.test(urlDate)) {
      const parsed = new Date(`${urlDate}T00:00:00`);
      if (!isNaN(parsed.getTime())) {
        setCursor(parsed);
      }
    }
  }

  const urlView = searchParams.get("view");
  const [prevUrlView, setPrevUrlView] = useState(urlView);
  if (urlView !== prevUrlView) {
    setPrevUrlView(urlView);
    if (urlView === "week" || urlView === "month") {
      setView(urlView);
    }
  }

  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_CURSOR_KEY, dateKeyOf(cursor));
    } catch {
      // ignore
    }
  }, [cursor]);

  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_VIEW_KEY, view);
    } catch {
      // ignore
    }
  }, [view]);

  const range = useMemo(() => visibleRange(cursor, view), [cursor, view]);
  const lessons = useQuery(api.lessons.listRange, range);

  // Retain whether the previous loaded view was empty while a new range query
  // is in-flight. This stops the hint from vanishing and immediately reappearing
  // (causing the calendar grid below it to bounce) when navigating between empty ranges.
  const [prevEmpty, setPrevEmpty] = useState<boolean | null>(null);
  const [prevLessons, setPrevLessons] = useState(lessons);

  if (lessons !== prevLessons) {
    setPrevLessons(lessons);
    if (lessons !== undefined) {
      setPrevEmpty(lessons.length === 0);
    }
  }

  const isEmpty = lessons !== undefined ? lessons.length === 0 : (prevEmpty ?? false);

  const lessonsByDate = useMemo(() => {
    const grouped: Record<string, CalendarLesson[]> = {};
    for (const lesson of lessons ?? []) {
      const list = grouped[lesson.date] ?? (grouped[lesson.date] = []);
      list.push({
        id: lesson._id,
        title: lesson.title ?? lesson.subjectName,
        shortCode: lesson.subjectShortCode,
        startMinute: lesson.startMinute,
        endMinute: lesson.endMinute,
        room: lesson.location,
        category: lesson.category,
        status: lesson.status as LessonStatus,
        color: lesson.subjectColor,
        cancelled: lesson.cancelled,
        partialAudio: lesson.partialAudio,
        needsReview: lesson.needsReview,
        notes: lesson.notes,
      });
    }
    return grouped;
  }, [lessons]);

  return (
    <div className="-mx-3.5 -mt-4 -mb-24 sm:-mx-10 sm:-my-8 sm:mb-0">
      <AnimatePresence initial={false}>
        {isEmpty && (
          <motion.div
            key="calendar-empty-hint"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <EmptyHint />
          </motion.div>
        )}
      </AnimatePresence>
      <LiquidCalendar
        lessonsByDate={lessonsByDate}
        cursor={cursor}
        onCursorChange={setCursor}
        view={view}
        onViewChange={setView}
        loading={lessons === undefined}
        renderLessonExtra={(lesson, date) => (
          <RecorderPanel
            lessonId={lesson.id}
            lessonLabel={`${lesson.title}${lesson.category ? ` ${lesson.category.toLowerCase()}` : ""} · ${date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}`}
          />
        )}
      />
    </div>
  );
}

/**
 * An empty range is ambiguous - a holiday week looks exactly like a timetable
 * that was never entered - so this points at the schedule editor rather than
 * claiming anything about why the grid is bare.
 */
function EmptyHint() {
  return (
    <div className="border-b border-neutral-200 bg-[#fbfbfa] px-4 py-2.5 font-mono text-[11px] text-neutral-500 dark:border-neutral-800 dark:bg-[#171716] dark:text-neutral-400 sm:px-8">
      No lessons in this range.{" "}
      <Link to="/schedule" className="underline underline-offset-2 hover:text-neutral-800 dark:hover:text-neutral-200">
        Add subjects and weekly rules
      </Link>{" "}
      and they will appear here.
    </div>
  );
}
